import React, { useCallback, useEffect, useState, useContext } from 'react';
import { AccordionTrigger, AccordionContent } from 'components/common/Accordion'
import * as Accordion from '@radix-ui/react-accordion';
import EditUserForm from './EditUserForm';
import SaveCancelButton from '../../manage-groups/edit-group/SaveCancelButton';
import PasswordExpiryFormEdit from '../PasswordExpiryRadio';
import EditUserGroups from './EditUserGroups';
import { redirectIFrame } from '../../../helpers/iframeHelpers';
import { zxcvbn } from '../../../../../js/zxcvbn';
import { useDispatch } from 'react-redux';
import { actionError, actionSuccess } from '../../../actions/viewActions';
import { resToJsonHandleErrors } from '../../../helpers/actionHelpers';
import LocaleContext from '../../../constants/localization';
import PasswordExpiryRadio from '../PasswordExpiryRadio';

const EditView = ({ selectedGroup, handleCancel }) => {
  const LS = useContext(LocaleContext);
  const dispatch = useDispatch();

  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [passwordExpiryDate, setPasswordExpiryDate] = useState('');
  const [passwordExpiryInMonths, setPasswordExpiryInMonths] = useState(0);
  const [passwordExpiryRadio, setPasswordExpiryRadio] = useState(0);

  const [groups, setGroups] = useState({});
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [ssoGroups, setSsoGroups] = useState({});

  const [roles, setRoles] = useState({});
  const [assignedRoles, setAssignedRoles] = useState([]);

  const [initialPassive, setInitialPassive] = useState(false);

  const [confirmOnCancel, setConfirmOnCancel] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  const [openAccordions, setOpenAccordions] = useState([]);

  // DAM#355 Sort groups based on name in alphabetical order
  function sortGroupsInAlphabeticalOrder(group) {
    return Object.fromEntries(
      Object.entries(group)
      .sort(([,valueA], [,valueB]) => {
        return valueA.localeCompare(valueB);
      })
    )
  }

  useEffect(async () => {
    const formData = new URLSearchParams();
    formData.append('id', selectedGroup);

    await fetch('manageUsers.do?action=showUserReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then((j) => {
        setUsername(j.user.login);
        setName(j.user.name);
        setDescription(j.user.description);
        setEmail(j.user.email);

        setPasswordExpiryInMonths(j.passwordExpiry.expiresIn);
        setPasswordExpiryDate(
          j.passwordExpiry.expireDate != null ? j.passwordExpiry.expireDate : ''
        );

        setPasswordExpiryRadio(!!j.passwordExpiry.expiresIn);

        setGroups(sortGroupsInAlphabeticalOrder(j.allGroups));
        setAssignedGroups(j.assignedGroups);

        setRoles(j.roles);
        setAssignedRoles(j.assignedRoles);

        setSsoGroups(j.ssoGroups);

        setInitialPassive(j.passive);

        setDataFetched(true);
      })
      .catch((e) => dispatch(actionError(e)));
  }, []);
  useEffect(() => {
    if (dataFetched) setConfirmOnCancel(true);
  }, [
    name,
    email,
    description,
    assignedRoles,
    assignedGroups,
    passwordExpiryInMonths,
  ]);

  const handleDelete = async (id) => {
    if (!confirm('Haluatko varmasti poistaa käyttäjän?')) return;
    const formData = new URLSearchParams();
    formData.append('id', id);

    await fetch('manageUsers.do?action=deleteUserReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then(() => {
        dispatch(actionSuccess('userDeleted'));
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new URLSearchParams();
    formData.append('id', selectedGroup);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    if (email) formData.append('email', email);
    if (passwordExpiryInMonths)
      formData.append('passwordExpiryMonths', passwordExpiryInMonths);
    assignedRoles.forEach((ar) => formData.append('roles', ar));
    assignedGroups.forEach((ag) => formData.append('assignedGroups', ag));

    await fetch('manageUsers.do?action=saveUserReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then(() => {
        dispatch(actionSuccess('userSaved'));
        handleCancel();
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    const formData = new URLSearchParams();
    formData.append('id', selectedGroup);
    formData.append('password', password);
    fetch('manageUsers.do?action=changePasswordReact', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    })
      .then(resToJsonHandleErrors)
      .then((json) => {
        setOpenAccordions(openAccordions.filter((a) => a != 'password'));
        dispatch(actionSuccess('userPasswordChanged'));
      })
      .catch((e) => dispatch(actionError(e)));
  };

  const handleAssignedRolesChange = useCallback(
    (id) => {
      if (assignedRoles?.includes(id))
        setAssignedRoles(assignedRoles.filter((i) => i !== id));
      else setAssignedRoles([...assignedRoles, id]);
    },
    [assignedRoles, setAssignedRoles]
  );

  const handleAssignedGroupsChange = useCallback(
    (id) => {
      if (assignedGroups?.includes(id))
        setAssignedGroups(assignedGroups.filter((i) => i !== id));
      else setAssignedGroups([...assignedGroups, id]);
    },
    [assignedGroups, setAssignedGroups]
  );

  return (
    <form onSubmit={(e) => handleSave(e)}>
      <h2>{username}</h2>
      <SaveCancelButton
        handleCancel={() => {
          if (confirmOnCancel && !confirm(LS.manageGroups.confirmCancel))
            return;
          handleCancel();
        }}
      />
      <EditUserForm
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        email={email}
        setEmail={setEmail}
      ></EditUserForm>
      <hr />
      <a
        style={{ cursor: 'pointer' }}
        onClick={() => redirectIFrame(`eventLog.do?target=${username}`)}
      >
        {LS.manageUsers.log}
      </a>
      <hr />
      <h3>{LS.manageUsers.userPassword}</h3>
      {passwordExpiryDate && (
        <>
          {LS.manageUsers.userPasswordExpires}
          {passwordExpiryDate}
        </>
      )}
      <h4>{LS.manageUsers.changeUserPasswordExpiry}</h4>
      <PasswordExpiryRadio
        edit
        passwordExpiryInMonths={passwordExpiryInMonths}
        setPasswordExpiryInMonths={setPasswordExpiryInMonths}
        passwordExpiryRadio={passwordExpiryRadio}
        setPasswordExpiryRadio={setPasswordExpiryRadio}
      />
      <Accordion.Root
        type="multiple"
        collapsible="true"
        value={openAccordions}
        onValueChange={setOpenAccordions}
      >
        <Accordion.Item value="password">
          <AccordionTrigger>
            <span>
              <i className="fa fa-lock"></i>
              {LS.manageUsers.changeUserPassword}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="password">{LS.manageUsers.password}</label>
              <input
                name="password"
                onChange={(e) => setPassword(e.target.value)}
                type={passwordVisible ? 'text' : 'password'}
                required
              />
              <a onClick={() => setPasswordVisible(!passwordVisible)}>
                {passwordVisible ? 'Piilota salasana' : 'Näytä salasana'}
              </a>
            </div>
            <button
              className="button"
              type="submit"
              onClick={(e) => handlePasswordChange(e)}
            >
              {LS.manageUsers.changeUserPassword}
            </button>
          </AccordionContent>
        </Accordion.Item>
      </Accordion.Root>
      <hr />
      <Accordion.Root type="multiple" collapsible="true">
        <Accordion.Item value="roles">
          <AccordionTrigger>{LS.manageUsers.roles}</AccordionTrigger>
          <AccordionContent>
            <EditUserGroups
              roles={roles}
              assignedRoles={assignedRoles}
              handleAssignedRolesChange={handleAssignedRolesChange}
            />
          </AccordionContent>
        </Accordion.Item>
        <Accordion.Item value="groups">
          <AccordionTrigger>{LS.manageUsers.groups}</AccordionTrigger>
          <AccordionContent>
            <EditUserGroups
              roles={groups}
              assignedRoles={assignedGroups}
              handleAssignedRolesChange={handleAssignedGroupsChange}
              links
            />
          </AccordionContent>
        </Accordion.Item>
      </Accordion.Root>
      <hr />
      <br />
      <button
        className="button"
        type="button"
        disabled={!initialPassive}
        onClick={() => handleDelete(selectedGroup)}
      >
        {LS.manageUsers.delete}
      </button>
      <SaveCancelButton
        handleCancel={() => {
          if (confirmOnCancel && !confirm(LS.manageGroups.confirmCancel))
            return;
          handleCancel();
        }}
      />
    </form>
  );
};

export default EditView;
